/**
 * sl-bpu6.2.1: useWorkspaceCustomApps — SWR hook for the list-view page.
 *
 * Wraps the `customApps(workspaceId)` query. Unlike the global
 * {@link useQuery} wrapper this variant does NOT use suspense — the list-page
 * renders its own skeleton while `isLoading` is true.
 *
 * Cache key `['customApps', workspaceId]` is stable so card mutations can
 * revalidate via `mutate()`. Workspace metadata events also invalidate this
 * cache, keeping editor/detail and app-area consumers aligned with the
 * canonical launch projection used by the overview.
 */
import { GraphQLService } from '@affine/core/modules/cloud';
import { WorkspaceRealtimeService } from '@affine/core/modules/editor';
import { type CustomAppsQuery, customAppsQuery } from '@affine/graphql';
import { useService } from '@toeverything/infra';
import type { GraphQLError } from 'graphql';
import { useEffect } from 'react';
import type { SWRResponse } from 'swr';
import useSWR from 'swr';

import {
  FEATURE_KEY_CUSTOM_APPS_CREATE,
  useFeatureGate,
} from './use-feature-gate';

export type CustomAppInfo = CustomAppsQuery['customApps'][number];

export interface UseWorkspaceCustomAppsResult {
  views: CustomAppInfo[] | undefined;
  isLoading: boolean;
  isValidating: boolean;
  error: GraphQLError | undefined;
  mutate: SWRResponse<CustomAppInfo[], GraphQLError>['mutate'];
}

const SERVER_PROVISIONING_REFRESH_INTERVAL_MS = 500;

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

function hasPendingServerProvisioning(view: CustomAppInfo): boolean {
  const config = asRecord(view.config);
  const provisioning = asRecord(config?.provisioning);
  return provisioning?.status === 'pending';
}

export function useWorkspaceCustomApps(
  workspaceId: string | null
): UseWorkspaceCustomAppsResult {
  const graphql = useService(GraphQLService);
  const workspaceRealtimeService = useService(WorkspaceRealtimeService);
  const { isFeatureAllowed, isLoading: isFeatureGateLoading } =
    useFeatureGate();
  const canQueryCustomApps =
    !isFeatureGateLoading && isFeatureAllowed(FEATURE_KEY_CUSTOM_APPS_CREATE);

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    CustomAppInfo[],
    GraphQLError
  >(
    workspaceId && canQueryCustomApps ? ['customApps', workspaceId] : null,
    async () => {
      const res = await graphql.gql({
        query: customAppsQuery,
        variables: { workspaceId: workspaceId as string },
      });
      return res.customApps;
    },
    {
      // Global SWRConfig sets suspense:true — opt-out so the list-page can
      // render its own loading/error UI instead of suspending the route.
      suspense: false,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: latestViews =>
        latestViews?.some(hasPendingServerProvisioning)
          ? SERVER_PROVISIONING_REFRESH_INTERVAL_MS
          : 0,
      shouldRetryOnError: error => !isFeatureUnavailableError(error),
    }
  );

  useEffect(() => {
    if (!workspaceId || !canQueryCustomApps) return;
    const releaseWorkspacePresence =
      workspaceRealtimeService.retainWorkspacePresence();
    const subscription =
      workspaceRealtimeService.workspaceMetaChanged$.subscribe(event => {
        if (
          event &&
          event.workspaceId === workspaceId &&
          event.changeType.startsWith('custom_app_')
        ) {
          void mutate();
        }
      });
    return () => {
      subscription.unsubscribe();
      releaseWorkspacePresence();
    };
  }, [canQueryCustomApps, mutate, workspaceId, workspaceRealtimeService]);

  return {
    views: data,
    isLoading: canQueryCustomApps ? isLoading : isFeatureGateLoading,
    isValidating,
    error: isFeatureUnavailableError(error) ? undefined : error,
    mutate,
  };
}

function isFeatureUnavailableError(error: unknown): boolean {
  if (!error) return false;
  const code = (error as { extensions?: { code?: unknown } }).extensions?.code;
  if (code === 'FEATURE_NOT_AVAILABLE') return true;

  const graphQLErrors = (
    error as
      | {
          graphQLErrors?: Array<{
            extensions?: { code?: unknown };
          }>;
        }
      | undefined
  )?.graphQLErrors;
  return (
    graphQLErrors?.some(
      graphQLError => graphQLError.extensions?.code === 'FEATURE_NOT_AVAILABLE'
    ) ?? false
  );
}
